// The Agents screen part: the nav item, /me/agents (the list beside the open
// chat), /me/agents/new and /me/agents/<key> (with a side chat or a side view
// at its right), /me/agents/turn-ui/<item> and /me/agents/turn-draft/<item>
// (an item's UI, a draft's text alone, without the frame, for a host's own
// view of it), the events that change what it shows (including the live
// words of a reply and a run's progress), the favorite kind `agent_chat` and
// its side view, the "chat of this Case" button in the Case's header, and the
// keys `n` (a new chat) and `g g` (Agents, the old web's key).
//
// Promise:
// - A chat opens by its key. A path naming its id (favorites and references
//   point at ids) is replaced by the key's path.
// - Every payload names the chat; handlers read again exactly that chat's
//   entries, turn list, runs or requests (never by prefix guessing), and the
//   chat itself, put into the loaded lists in place (the Agents list is never
//   read again for an event: see queries.ts).
import type { QueryClient } from "@tanstack/react-query";
import {
  createRoute,
  Outlet,
  redirect,
  useParams,
} from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import type { RunLive } from "@trameria/shared-agent";
import { lazyScreen, withLazy } from "../app/lazy";
import type { MeRouterContext } from "../app/routes";
import { defineScreenPart } from "../app/screen-part";
import { refPrefix } from "../refs/queries";
import { ListDetail } from "../shell/list-detail";
import { iconOf } from "../ui/icon";
import {
  caseChatAction,
  caseChatRowMark,
  stepChatRowMark,
} from "./case-chat-action";
import {
  agentsPrefix,
  caseStepsPrefix,
  chatChanged,
  chatKey,
  chatQuery,
  chatRead,
  devicesKey,
  devicesQuery,
  entriesQuery,
  entryChanged,
  fetchEntriesFrom,
  progressCame,
  putLiveProgress,
  putLiveReply,
  requestChanged,
  runChanged,
  turnChanged,
  turnQuery,
} from "./queries";
import { thisPcSettings } from "./this-pc";

// The screens are loaded when first opened (app/lazy.tsx).
const AgentsList = lazyScreen(() => import("./list"), "AgentsList");
const ChatRoute = lazyScreen(() => import("./chat-screen"), "ChatRoute");
const ChatSideView = lazyScreen(() => import("./view"), "ChatSideView");
const TurnUiPage = lazyScreen(() => import("./turn/ui-page"), "TurnUiPage");
const TurnDraftPage = lazyScreen(
  () => import("./turn/draft-page"),
  "TurnDraftPage",
);

const str = (v: unknown) => (typeof v === "string" ? v : undefined);
const fieldsOf = (payload: unknown) =>
  (payload ?? {}) as Record<string, unknown>;

/**
 * The list alone, or beside the open chat. Beside it, the chat keeps a
 * document's width, which holds a side chat or a side view next to the chat;
 * narrower, the list goes away first (back to it from the header's top-left
 * button) and then the side comes in over the chat.
 */
function AgentsScreen() {
  const { key } = useParams({ strict: false }) as { key?: string };
  return (
    <ListDetail
      list={<AgentsList openKey={key && key !== "new" ? key : null} />}
      detail={<Outlet />}
      showing={key ? "detail" : "list"}
      listPath="/me/agents"
      listLabel={m.agents_list()}
    />
  );
}

const dropRef = (qc: QueryClient, chatId: string | undefined) => {
  if (chatId)
    void qc.invalidateQueries({ queryKey: refPrefix("agent_chat", chatId) });
};

const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const agentsScreen = defineScreenPart({
  id: "agents",
  nav: {
    order: 30,
    label: () => m.agents_nav(),
    icon: iconOf("agent"),
    to: "/me/agents",
    remembers: true,
  },
  routes: (main) => {
    const agents = createRoute({
      getParentRoute: () => main,
      path: "agents",
      component: withLazy(AgentsScreen, AgentsList),
    });
    // Nothing open: the list is alone and no detail is drawn.
    const none = createRoute({ getParentRoute: () => agents, path: "/" });
    const one = createRoute({
      getParentRoute: () => agents,
      path: "$key",
      // `?at=<seq>`: where to open the line (a jump from the Inbox to where an
      // item of "your turn" was answered). Given to someone else, it opens the
      // same place. `?item=<id>`: the item of "your turn" to open there (open
      // to answer, or a closed one shown with what was asked).
      validateSearch: (
        s: Record<string, unknown>,
      ): { at?: number; item?: string } => {
        const at = Number(s.at);
        const item = typeof s.item === "string" && s.item ? s.item : undefined;
        return {
          ...(Number.isInteger(at) && at > 0 ? { at } : {}),
          ...(item ? { item } : {}),
        };
      },
      loaderDeps: ({ search }) => ({ at: search.at }),
      // A missing chat is told in the detail, not by stopping the route.
      loader: async ({ context, params, deps }) => {
        const c = context as MeRouterContext;
        const devices = c.queryClient
          .ensureQueryData(devicesQuery(c.api))
          .catch(() => undefined);
        if (params.key === "new") {
          await devices;
          return;
        }
        const chat = await c.queryClient
          .ensureQueryData(chatQuery(c.api, params.key))
          .catch(() => null);
        if (chat && chat.key !== params.key && ID_RE.test(params.key)) {
          c.queryClient.setQueryData(chatKey(chat.key), chat);
          throw redirect({
            to: "/me/agents/$key",
            params: { key: chat.key },
            replace: true,
          });
        }
        if (chat)
          await Promise.all([
            devices,
            c.queryClient
              .ensureQueryData(entriesQuery(c.api, chat.id))
              .then((data) =>
                // The page holding `at`, when it is not read yet (the pages
                // between come as the person scrolls to them).
                deps.at && !data.items.some((e) => e.seq === deps.at)
                  ? fetchEntriesFrom(c.api, c.queryClient, chat.id, deps.at)
                  : undefined,
              )
              .catch(() => undefined),
            c.queryClient
              .ensureQueryData(turnQuery(c.api, chat.id))
              .catch(() => undefined),
          ]);
      },
      component: ChatRoute,
    });
    // The side chat of a reply, beside the chat (the chat's screen draws it).
    const side = createRoute({
      getParentRoute: () => one,
      path: "side/$entryId",
      component: () => null,
    });
    // A thing opened at the right of the chat (the chat's screen draws it).
    const view = createRoute({
      getParentRoute: () => one,
      path: "view/$viewKind/$viewId",
      component: () => null,
    });
    return [agents.addChildren([none, one.addChildren([side, view])])];
  },
  bareRoutes: (me) => {
    // An item's UI alone, for a host that draws the item in its post box
    // and shows the UI in a web view of its own (turn/ui-page.tsx).
    // `?chat=<id>`: the chat whose list holds the item (it is read there).
    const turnUi = createRoute({
      getParentRoute: () => me,
      path: "agents/turn-ui/$item",
      validateSearch: (s: Record<string, unknown>): { chat?: string } =>
        typeof s.chat === "string" && s.chat ? { chat: s.chat } : {},
      loaderDeps: ({ search }) => ({ chat: search.chat }),
      loader: async ({ context, deps }) => {
        const c = context as MeRouterContext;
        if (deps.chat)
          await c.queryClient
            .ensureQueryData(turnQuery(c.api, deps.chat))
            .catch(() => undefined);
      },
      component: withLazy(function TurnUiRoute() {
        const { item } = turnUi.useParams();
        const { chat } = turnUi.useSearch();
        return <TurnUiPage itemId={item} chatId={chat ?? ""} />;
      }, TurnUiPage),
    });
    // One side of a draft alone, written in the web's frame
    // (turn/draft-page.tsx); `?chat=` as above, `?side=` the side shown.
    const turnDraft = createRoute({
      getParentRoute: () => me,
      path: "agents/turn-draft/$item",
      validateSearch: (
        s: Record<string, unknown>,
      ): { chat?: string; side?: "source" } => ({
        ...(typeof s.chat === "string" && s.chat ? { chat: s.chat } : {}),
        ...(s.side === "source" ? { side: "source" as const } : {}),
      }),
      loaderDeps: ({ search }) => ({ chat: search.chat }),
      loader: async ({ context, deps }) => {
        const c = context as MeRouterContext;
        if (deps.chat)
          await c.queryClient
            .ensureQueryData(turnQuery(c.api, deps.chat))
            .catch(() => undefined);
      },
      component: withLazy(function TurnDraftRoute() {
        const { item } = turnDraft.useParams();
        const { chat, side } = turnDraft.useSearch();
        return (
          <TurnDraftPage
            itemId={item}
            chatId={chat ?? ""}
            side={side ?? "text"}
          />
        );
      }, TurnDraftPage),
    });
    return [turnUi, turnDraft];
  },
  realtime: {
    "agent_chat.changed": (payload, { qc, api }) => {
      const p = fieldsOf(payload);
      chatChanged(qc, api, str(p.chatId), str(p.change));
      dropRef(qc, str(p.chatId));
    },
    "agent_chat.entry": (payload, { qc, api }) => {
      const p = fieldsOf(payload);
      const chatId = str(p.chatId);
      if (chatId && typeof p.seq === "number")
        entryChanged(qc, api, chatId, p.seq, str(p.kind));
    },
    // Read here or in another window: kept without reading the chat again.
    "agent_chat.read": (payload, { qc }) => {
      const p = fieldsOf(payload);
      const chatId = str(p.chatId);
      if (chatId && typeof p.seq === "number") chatRead(qc, chatId, p.seq);
    },
    "agent_chat.run": (payload, { qc, api }) => {
      const chatId = str(fieldsOf(payload).chatId);
      if (chatId) runChanged(qc, api, chatId);
    },
    "agent_chat.request": (payload, { qc }) => {
      const chatId = str(fieldsOf(payload).chatId);
      if (chatId) requestChanged(qc, chatId);
    },
    "agent_turn.changed": (payload, { qc, api }) => {
      const p = fieldsOf(payload);
      turnChanged(
        qc,
        api,
        [str(p.chatId), str(p.holderChatId)].filter((x): x is string => !!x),
      );
    },
    "agent_device.changed": (_payload, { qc }) =>
      void qc.invalidateQueries({ queryKey: devicesKey }),
    // An agent connected or went away: whether it is connected is read again.
    "agent_device.presence": (_payload, { qc }) =>
      void qc.invalidateQueries({ queryKey: devicesKey }),
    // The title of the Action a chat works on comes from its Case.
    "case.changed": (_payload, { qc }) =>
      void qc.invalidateQueries({ queryKey: caseStepsPrefix }),
    "case.deleted": (_payload, { qc }) =>
      void qc.invalidateQueries({ queryKey: caseStepsPrefix }),
    "agent_chat.reply": (payload, { qc }) => {
      const p = fieldsOf(payload);
      const chatId = str(p.chatId);
      const runId = str(p.runId);
      if (!chatId || !runId || typeof p.text !== "string") return;
      putLiveReply(qc, {
        chatId,
        runId,
        entryId: str(p.entryId) ?? null,
        text: p.text,
      });
    },
    "agent_chat.progress": (payload, { qc, api }) => {
      const p = fieldsOf(payload);
      const chatId = str(p.chatId);
      const runId = str(p.runId);
      if (!chatId || !runId || !p.live) return;
      putLiveProgress(qc, { chatId, runId, live: p.live as RunLive });
      progressCame(qc, api, chatId, runId);
    },
  },
  favoriteKinds: {
    agent_chat: {
      icon: iconOf("agentChat"),
      to: (id) => `/me/agents/${id}`,
      refPaths: ["/me/agents/:key", "/me/agents/:key/*"],
    },
    // An item of the owner's turn (what a Case's own check waits on): it opens
    // the chat whose list holds it, and a waiting names it by where to answer
    // (its title is the waiting's own).
    turn: {
      // The Agents mark, as the old web's waiting row.
      icon: iconOf("agent"),
      to: (_id, path) => {
        const chat = path?.chatKey ?? path?.chatId;
        return typeof chat === "string" ? `/me/agents/${chat}` : "/me/agents";
      },
      waitName: ({ state }) =>
        state === "open"
          ? m.agents_wait_turn_open()
          : m.agents_wait_turn_closed(),
    },
  },
  settings: thisPcSettings,
  headerActions: { case: [caseChatAction] },
  rowMarks: { case: [caseChatRowMark], step: [stepChatRowMark] },
  sideViews: { agent_chat: ChatSideView },
  shortcuts: [
    {
      id: "agents.new",
      key: "n",
      group: "create",
      label: () => m.agents_new(),
      run: ({ navigate }) => navigate("/me/agents/new"),
    },
    {
      id: "agents.go",
      key: "g g",
      group: "navigate",
      label: () => m.agents_go(),
      run: ({ navigate }) => navigate("/me/agents"),
    },
  ],
  // What cloud draws right after a reload: the list, the open chat, its line and its turn list.
  persist: (key) =>
    key[0] === agentsPrefix[0] &&
    key[1] === agentsPrefix[1] &&
    (key[2] === "list" ||
      key[2] === "chat" ||
      key[2] === "entries" ||
      key[2] === "turn"),
});
